import {
  ArrowBack as ArrowBackIcon,
  Article as ArticleIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Favorite as FavoriteIcon,
  Newspaper as NewspaperIcon,
  Send as SendIcon,
  ShoppingBag as ShoppingBagIcon
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardMedia,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Loading from '../components/Loading';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Post, postApi } from '../services/postApi';
import { colors } from '../theme';

const getTypeIcon = (type: string) => {
  switch (type) {
    case 'news': return <NewspaperIcon />;
    case 'product': return <ShoppingBagIcon />;
    default: return <ArticleIcon />;
  }
};

const getTypeLabel = (type: string) => {
  switch (type) {
    case 'news': return 'Haber';
    case 'product': return 'Ürün';
    default: return 'Yazı';
  }
};

const formatTimeAgo = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
};

const PostDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userRole, clientId, dietitianId, client, dietitian, loading: authLoading } = useAuth();
  const { showError } = useToast();
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [commentContent, setCommentContent] = useState('');

  const isLoggedIn = !!userRole && !!(userRole === 'client' ? clientId : dietitianId);
  const currentUserId = userRole === 'client' ? clientId : dietitianId;
  const currentUserName = userRole === 'client'
    ? (client ? `${client.firstName || ''} ${client.lastName || ''}`.trim() || client.email : 'Danışan')
    : (dietitian ? `${dietitian.firstName || ''} ${dietitian.lastName || ''}`.trim() : 'Diyetisyen');

  useEffect(() => {
    if (authLoading) return;
    if (!isLoggedIn && id) {
      navigate(`/client/login?returnUrl=${encodeURIComponent(`/feed/${id}`)}`, { replace: true });
      return;
    }
    if (!id) {
      navigate('/feed', { replace: true });
      return;
    }
    const load = async () => {
      try {
        const data = await postApi.getPostById(id);
        setPost(data);
      } catch (e) {
        console.error(e);
        showError('Paylaşım yüklenemedi');
        navigate('/feed', { replace: true });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, authLoading, isLoggedIn, navigate, showError]);

  const handleToggleLike = async () => {
    if (!post || !currentUserId) return;
    try {
      const updated = await postApi.toggleLike(post.id, currentUserId);
      setPost(updated);
    } catch (e) {
      showError('Beğeni işlemi başarısız');
    }
  };

  const handleAddComment = async () => {
    if (!post || !currentUserId || !commentContent.trim()) return;
    try {
      const updated = await postApi.addComment(post.id, currentUserId, currentUserName, commentContent.trim());
      setPost(updated);
      setCommentContent('');
    } catch (e) {
      showError('Yorum eklenemedi');
    }
  };

  if (authLoading || (!isLoggedIn && id)) return null;
  if (loading) return <Loading message="Paylaşım yükleniyor..." />;
  if (!post) return null;

  const isLiked = post.likedBy?.includes(currentUserId || '') ?? false;

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', py: 2, px: 2 }}>
      <Button
        startIcon={<ArrowBackIcon sx={{ width: 'fit-content' }} />}
        onClick={() => navigate('/feed')}
        sx={{
          mb: 2,
          textTransform: 'none',
          color: colors.text.secondary,width:'fit-content',
          '&:hover': { bgcolor: colors.background?.light || 'rgba(0,0,0,0.04)', color: colors.primary.main },
        }}
      >
        Paylaşımlara Dön
      </Button>
      <Card elevation={0} sx={{ border: '1px solid #eee', borderRadius: 2 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Avatar sx={{ width: 40, height: 40, bgcolor: colors.primary.main }}>
              {(post.dietitianName || 'D').charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>{post.dietitianName}</Typography>
              <Typography variant="caption" color="text.secondary">{formatTimeAgo(post.createdAt)}</Typography>
            </Box>
          </Box>
          <Chip icon={getTypeIcon(post.type)} label={getTypeLabel(post.type)} size="small" sx={{ bgcolor: colors.alpha?.primary10 || '#e8f5e9', color: colors.primary.main ,width:'fit-content'}} />
        </Box>
        {post.imageUrl && (
          <CardMedia component="img" image={post.imageUrl} alt={post.title} sx={{ width: '100%', maxHeight: 400, objectFit: 'cover' }} />
        )}
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>{post.title}</Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{post.content}</Typography>
          {post.tags && post.tags.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {post.tags.map((tag) => (
                <Typography key={tag} component="span" variant="body2" sx={{ color: colors.primary.main, mr: 1 }}>#{tag}</Typography>
              ))}
            </Box>
          )}
          {post.type === 'product' && (post.price != null || post.productUrl) && (
            <Box sx={{ mt: 2 }}>
              {post.price != null && (
                <Typography variant="h6" fontWeight={700} color={colors.primary.main}>
                  ₺{post.price.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                </Typography>
              )}
              {post.productUrl && (
                <Button href={post.productUrl} target="_blank" size="small" sx={{ textTransform: 'none' }}>Ürünü İncele →</Button>
              )}
            </Box>
          )}
        </Box>
        <Box sx={{ px: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" onClick={handleToggleLike} sx={{ color: isLiked ? '#ed4956' : '#262626' ,width:'fit-content'}}>
            {isLiked ? <FavoriteIcon sx={{ width: 'fit-content' }} /> : <FavoriteBorderIcon sx={{ width: 'fit-content' }} />}
          </IconButton>
          <Typography variant="body2" fontWeight={600} sx={{ width: 'fit-content' }}>{post.likeCount ?? 0} beğeni</Typography>
        </Box>
        {post.comments && post.comments.length > 0 && (
          <Box sx={{ px: 2, pb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Yorumlar</Typography>
            {post.comments.map((c) => (
              <Paper key={c.id} elevation={0} sx={{ p: 1.5, mb: 1, bgcolor: '#fafafa', borderRadius: 1 }}>
                <Typography variant="body2" fontWeight={600}>{c.userName}</Typography>
                <Typography variant="body2">{c.content}</Typography>
              </Paper>
            ))}
          </Box>
        )}
        <Box sx={{ borderTop: '1px solid #eee', p: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Yorum ekle..."
            value={commentContent}
            onChange={(e) => setCommentContent(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleAddComment} disabled={!commentContent.trim()} sx={{ color: colors.primary.main ,width:'fit-content'}}>
                    <SendIcon sx={{ width: 'fit-content' }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' } }}
          />
        </Box>
      </Card>
    </Box>
  );
};

export default PostDetailPage;
